'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string;
  email: string;
  phone: string;
  serviceType: string;
  message: string;
  preferredContact: string;
}

interface ContactFormProps {
  currentLanguage: 'fr' | 'de';
}

const ContactForm = ({ currentLanguage }: ContactFormProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    serviceType: '',
    message: '',
    preferredContact: 'email'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const content = {
    fr: {
      title: "Envoyez-nous un message",
      subtitle: "Remplissez le formulaire ci-dessous et nous vous répondrons rapidement",
      nameLabel: "Nom complet",
      namePlaceholder: "Jean Dupont",
      emailLabel: "Adresse email",
      emailPlaceholder: "jean.dupont@example.com",
      phoneLabel: "Numéro de téléphone",
      phonePlaceholder: "+41 79 123 45 67",
      serviceLabel: "Type de service",
      servicePlaceholder: "Sélectionnez un service",
      serviceOptions: [
        "Nettoyage résidentiel",
        "Nettoyage commercial",
        "Nettoyage spécialisé",
        "Services de conciergerie",
        "Multi-services",
        "Autre"
      ],
      messageLabel: "Votre message",
      messagePlaceholder: "Décrivez vos besoins en détail...",
      preferredContactLabel: "Méthode de contact préférée",
      contactOptions: [
        { value: 'email', label: 'Email' },
        { value: 'phone', label: 'Téléphone' },
        { value: 'whatsapp', label: 'WhatsApp' }
      ],
      submitButton: "Envoyer le message",
      submittingButton: "Envoi en cours...",
      successMessage: "Message envoyé avec succès! Nous vous contacterons sous 24h.",
      errorMessage: "Une erreur s\'est produite. Veuillez réessayer.",
      requiredField: "Ce champ est obligatoire"
    },
    de: {
      title: "Senden Sie uns eine Nachricht",
      subtitle: "Füllen Sie das untenstehende Formular aus und wir werden Ihnen schnell antworten",
      nameLabel: "Vollständiger Name",
      namePlaceholder: "Max Mustermann",
      emailLabel: "E-Mail-Adresse",
      emailPlaceholder: "max.mustermann@example.com",
      phoneLabel: "Telefonnummer",
      phonePlaceholder: "+41 79 123 45 67",
      serviceLabel: "Art der Dienstleistung",
      servicePlaceholder: "Wählen Sie einen Service",
      serviceOptions: [
        "Wohnungsreinigung",
        "Gewerbliche Reinigung",
        "Spezialisierte Reinigung",
        "Concierge-Dienste",
        "Multi-Services",
        "Andere"
      ],
      messageLabel: "Ihre Nachricht",
      messagePlaceholder: "Beschreiben Sie Ihre Bedürfnisse im Detail...",
      preferredContactLabel: "Bevorzugte Kontaktmethode",
      contactOptions: [
        { value: 'email', label: 'E-Mail' },
        { value: 'phone', label: 'Telefon' },
        { value: 'whatsapp', label: 'WhatsApp' }
      ],
      submitButton: "Nachricht senden",
      submittingButton: "Wird gesendet...",
      successMessage: "Nachricht erfolgreich gesendet! Wir werden Sie innerhalb von 24 Stunden kontaktieren.",
      errorMessage: "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.",
      requiredField: "Dieses Feld ist erforderlich"
    }
  };

  const text = content[currentLanguage];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setSubmitStatus('success');
    
    // Reset form after success
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        serviceType: '',
        message: '',
        preferredContact: 'email'
      });
      setSubmitStatus('idle');
    }, 3000);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-surface">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="bg-card rounded-2xl shadow-lg p-8 lg:p-12 animate-pulse">
              <div className="h-8 bg-muted rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-muted rounded w-full mb-8"></div>
              <div className="space-y-6">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-12 bg-muted rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              {text.title}
            </h2>
            <p className="text-lg text-muted-foreground">
              {text.subtitle}
            </p>
          </div>

          <div className="bg-card rounded-2xl shadow-lg p-8 lg:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                  {text.nameLabel} <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Icon name="UserIcon" size={20} variant="outline" className="text-muted-foreground" />
                  </div>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder={text.namePlaceholder}
                    className="w-full pl-12 pr-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-300"
                  />
                </div>
              </div>

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                  {text.emailLabel} <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Icon name="EnvelopeIcon" size={20} variant="outline" className="text-muted-foreground" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder={text.emailPlaceholder}
                    className="w-full pl-12 pr-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-300"
                  />
                </div>
              </div>

              {/* Phone Field */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                  {text.phoneLabel}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Icon name="PhoneIcon" size={20} variant="outline" className="text-muted-foreground" />
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder={text.phonePlaceholder}
                    className="w-full pl-12 pr-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-300"
                  />
                </div>
              </div>

              {/* Service Type Field */}
              <div>
                <label htmlFor="serviceType" className="block text-sm font-medium text-foreground mb-2">
                  {text.serviceLabel} <span className="text-destructive">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Icon name="BriefcaseIcon" size={20} variant="outline" className="text-muted-foreground" />
                  </div>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-300 appearance-none bg-white"
                  >
                    <option value="">{text.servicePlaceholder}</option>
                    {text.serviceOptions.map((option, index) => (
                      <option key={index} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                    <Icon name="ChevronDownIcon" size={20} variant="outline" className="text-muted-foreground" />
                  </div>
                </div>
              </div>

              {/* Preferred Contact Method */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-3">
                  {text.preferredContactLabel}
                </label>
                <div className="flex flex-wrap gap-4">
                  {text.contactOptions.map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="preferredContact"
                        value={option.value}
                        checked={formData.preferredContact === option.value}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-primary focus:ring-2 focus:ring-ring"
                      />
                      <span className="text-sm text-foreground">{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Message Field */}
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                  {text.messageLabel} <span className="text-destructive">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={6}
                  placeholder={text.messagePlaceholder}
                  className="w-full px-4 py-3 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all duration-300 resize-none"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-primary hover:bg-secondary text-primary-foreground font-heading font-semibold py-4 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{text.submittingButton}</span>
                  </>
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={20} variant="solid" />
                    <span>{text.submitButton}</span>
                  </>
                )}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="flex items-start gap-3 p-4 bg-success/10 border border-success/30 rounded-lg">
                  <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-success flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-success-foreground">{text.successMessage}</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                  <Icon name="XCircleIcon" size={24} variant="solid" className="text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive-foreground">{text.errorMessage}</p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;